import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import beCancelPayment from 'actions/pos/beCancelPayment.action';
import { beFindReaderV1, beFindReaderV3 } from 'actions/pos/beFindReader.action';
import beGetPosIntentStatus from 'actions/pos/beGetPosIntentStatus.action';
import feDelPosIntentId from 'actions/pos/feDelPosIntentId.action';
import { CAP_POS_CANCEL, CAP_POS_LOOP, checkCapFlag } from 'bl/captures.bl';
import { LT_POS } from 'bl/loggers.bl';
import determinePosReaderId from 'bl/pos/determinePosReaderId.bl';
import { umsgCancelPayment } from 'bl/umsgs.bl';
import SalonFlag from 'components/core/SalonFlag.component';
import DialogMessage from 'components/dialog/DialogMessage.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import CardReadersModal from 'components/pos/CardReadersModal.component';
import CN from 'components/pos/PosReaderDialog.module.scss';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import FLAG from 'config/flag.singleton';
import PUSHER from 'config/pusher.singleton';
import SENTRY from 'config/sentry.singleton';
import DELAY from 'constants/delay.const';
import { POS_CODE, POS_FLOW_CODE } from 'constants/pos/posCode.const';
import SF from 'constants/salonFlag.const';
import useAfn from 'hooks/useAfn.hook';
import useCounter from 'hooks/useCounter.hook';
import useIsMountedRef from 'hooks/useIsMountedRef.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useSingleSalonFlag } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonFlags, selectCurrentSalonId, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import { selectRsBeInitReaderPayment } from 'selectors/request.selectors';
import asArray from 'utils/arrays/asArray.util';
import { firstOf } from 'utils/arrays/elements.util';
import fromAnyToErrorMessage from 'utils/converters/any/fromAnyToErrorMessage.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import call from 'utils/functions/call.util';
import delayed from 'utils/functions/delayed.util';
import loop from 'utils/functions/loop.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import tryAsync from 'utils/functions/tryAsync.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import setRef from 'utils/objects/setRef.util';
import isNotEnumVal from 'utils/predicates/enum/isNotEnumVal.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import isTruthy from 'utils/predicates/isTruthy.util';
import isPositiveInteger from 'utils/predicates/number/isPositiveInteger.util';
import reactChildrenLike from 'utils/react/childrenLike.util';
import reactChildrenUnlike from 'utils/react/childrenUnlike.util';

const CTAG = 'PosReaderDialog';
const L = logger(CTAG);
const L_POS = logger(LT_POS);

const USE_IS_CONFIRMING = true;
const READERS_LIMIT_TO_SHOW_PICKER_DIALOG = 2;

const PROCESSING_CODE = Object.freeze({
  done: 'result:pos.processing:done',
  error: 'result:pos.processing:error',
  unhandled: 'result:pos.processing:unhandled',
  waiting: 'result:pos.processing:waiting',
});

// if you need to mock the response, you can do something like
// const result = window.mockIntentStatus ? MOCK_INTENT_STATUS : await beGetPosIntentStatus()
// const MOCK_INTENT_STATUS = { response: { code: POS_FLOW_CODE.failure } }

const MSG_INIT = 'Connecting to POS reader...';
const MSG_CONNECT = `POS reader connected.`;
const MSG_WAITING = 'Initiated payment. Checking payment status...';
const MSG_SUCCESS = 'Payment successful';
const MSG_OFFLINE = 'The POS reader is not online';
const MSG_FAILED = 'Payment has failed. Please ask the client to try again or try another card.';

// NOTE: sometimes it may take people up to 5 minutes to find their card
// NOTE: the device itself may wait even hours, so a large number is OK
const MAX_ATTEMPTS = 1000;

const captureFailedCancel = ($) => {
  const { name, error, options, flags } = $ ?? {};

  const message = CAP_POS_CANCEL;
  const prefix = SF.PosReaderDialog;

  if (checkCapFlag({ prefix, flags }).error) return message;

  const logger = [CTAG, name];
  const tags = { function: name, ...asObject(options) };
  const extra = { ...tags, options };
  return SENTRY.error({ logger, tags, extra, error });
};

const captureLoopError = ($) => {
  const { name, error, options, flags } = $ ?? {};

  const message = CAP_POS_LOOP;
  const prefix = SF.PosReaderDialog;

  if (checkCapFlag({ prefix, flags }).error) return message;

  const logger = [CTAG, name];
  const tags = { function: name, ...asObject(options) };
  const extra = { ...tags, options };
  return SENTRY.error({ logger, tags, extra, error });
};

const processWaitingCode = (options) => {
  const tag = fromAnyToTrimmed(options?.tag) || 'processWaitingCode()';

  L.info(tag, MSG_WAITING);
  call('processWaitingCode() > options.showCancel()', options?.showCancel);

  L.debug('processWaitingCode()', tag, 'Waiting for customer action...');
};

const processFailureCode = (options) => {
  const tag = fromAnyToTrimmed(options?.tag) || 'processFailureCode()';

  L.info(tag, MSG_FAILED);
  call('processFailureCode() > options.showCancel()', options?.showCancel);
  call('processFailureCode() > options.showRetry()', options?.showRetry);

  const ev = { code: options.code, message: MSG_FAILED };
  call('processFailureCode() > options.signal()', options?.signal, PRDS.failure, ev);
};

const processSuccessCode = (options) => {
  const { paymentIntentId, setDoneEvent, hideCancel, showClose, signal, closeDelay, onDone, code, status, doneEvent } = options ?? {};

  const tag = fromAnyToTrimmed(options?.tag) || 'processFailureCode()';

  L.info(tag, MSG_SUCCESS);

  const evDoneImmediate = {
    code: POS_CODE.success,
    message: MSG_SUCCESS,
    paymentIntentId,
    status: PRDS.success,
    success: true,
  };

  if (USE_IS_CONFIRMING) setDoneEvent(evDoneImmediate);

  hideCancel();
  showClose();

  signal(PRDS.success, { code: POS_CODE.success, message: MSG_SUCCESS, paymentIntentId });

  if (!closeDelay) {
    L_POS.debug('PosReaderDialog.handleAttempt()', { closeDelay, evDoneImmediate });
    call(`${CTAG}.onDone`, onDone, evDoneImmediate);
    return;
  }

  const closeV1 = () => {
    // first status will change to success, then after a delay to closed
    signal(PRDS.closed, { code, message: MSG_SUCCESS, paymentIntentId });
    call(`${CTAG}.onDone`, onDone, evDoneImmediate);
  };

  const closeV2 = () => {
    // NOTE: if the close button was clicked, there is no need to fire the onDone event twice
    if (status === PRDS.closed) return;

    // first status will change to success, then after a delay to closed
    signal(PRDS.closed, { code, message: MSG_SUCCESS, paymentIntentId });

    // NOTE: should create the object here, since it might be missing from the component state at this point
    const evDoneDelayed = { code, message: MSG_SUCCESS, paymentIntentId, status: PRDS.success, success: true };
    L_POS.debug('PosReaderDialog.handleAttempt() > closeV2()', { doneEvent, evDoneDelayed });

    call(`${CTAG}.onDone`, onDone, evDoneDelayed);
  };

  delayed(closeDelay, USE_IS_CONFIRMING ? closeV1 : closeV2);
};

const processError = (options) => {
  setRef(options?.codeRef, POS_FLOW_CODE.status);
  options?.showCancel();
  options?.signal(PRDS.error, {
    code: deRef(options?.codeRef),
    message: options?.errorMessage,
    paymentIntentId: options?.paymentIntentId,
  });
};

const fromPusherDtoToPosPaymentEvent = (dto) => {
  const d1 = dto?.data;
  const d3 = dto?.data?.data?.data;

  return {
    status: d1?.status,
    actionType: fromAnyToString(d1?.action),
    message: fromAnyToString(d3?.message),
    code: fromAnyToString(d3?.code),
    intentId: fromAnyToString(d3?.intent_id),
    reason: fromAnyToString(d3?.reason),
  };
};

const fromDtoToPosPaymentEvent = (dto) => ({
  errorMessage: dto?.errorMessage,
  code: fromAnyToString(dto?.response?.code),
  message: fromAnyToString(dto?.response?.message),
});

const processFetchOrPusherResponse = (options) => {
  const { errorMessage, codeRef, showCancel, tag, code, message } = options ?? {};

  if (errorMessage) {
    processError(options);
    return { code: PROCESSING_CODE.error, message: errorMessage, tag };
  }

  L.debug(tag, 'code:', code, 'message:', message);
  setRef(codeRef, code);

  if (POS_CODE.success === code) {
    processSuccessCode(options);
    return { code: PROCESSING_CODE.done, message, tag };
  }

  if (POS_CODE.failure === code) {
    processFailureCode(options);
    return { code: PROCESSING_CODE.done, message, tag };
  }

  if (POS_CODE.waiting === code) {
    processWaitingCode(options);
    return { code: PROCESSING_CODE.waiting, message, tag };
  }

  showCancel();
  // TODO: use POS_FLOW_CODE to raise unknown as an error
  L.warn(tag, 'Unhandled code:', code, 'message:', message, 'options:', options);

  return { code: PROCESSING_CODE.unhandled, message, options, tag };
};

const PosReaderDialog = ({
  autoClose,
  beCancelPayment,
  beFindReaderV1,
  beFindReaderV3,
  beGetPosIntentStatus,
  children,
  feDelPosIntentId,
  manualStart,
  onAction,
  onChange,
  onDone,
  onInit,
  open,
  rsBeInitReaderPayment,
  salonFlags,
  salonId,
  price,
}) => {
  const isBeMultipleReadersEnabled = useSingleSalonFlag(SF.multiplePosReadersDialog);
  const isFeMultipleReadersEnabled = FLAG.multiplePosReadersDialog;
  const beFindReader = isBeMultipleReadersEnabled || isFeMultipleReadersEnabled ? beFindReaderV3 : beFindReaderV1;

  // eslint-disable-next-line no-nested-ternary
  const closeDelay = autoClose === false ? 0 : isPositiveInteger(autoClose) ? autoClose : DELAY.autohide;
  const autoStart = !manualStart;

  // reference is needed to track the change without triggering the useCallback
  const codeRef = useRef();
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();
  const { val: isGivenUp, yay: giveUp, nay: clearGiveUp } = useTip(false);
  const { val: isActive, yay: start, nay: stop } = useTip(false);
  const { val: attempts, inc: incrementAttempts } = useCounter(0);
  const [unexpectedError, setUnexpectedError] = useStateWhileMounted('');
  const [localReaderId, setLocalReaderId] = useStateWhileMounted('');
  const [localPaymentIntentId, setLocalPaymentIntentId] = useStateWhileMounted(null);

  const [status, setStatus] = useState(PRDS.closed);
  const [actives, setActives] = useState([]);
  const { val: isCancelAvailable, nay: hideCancel, yay: showCancel } = useTip(false);
  const { val: isCloseRendered, yay: showClose } = useTip(false);
  const { val: isRetryRendered, yay: showRetry, nay: hideRetry } = useTip(false);

  const [doneEvent, setDoneEvent] = useStateWhileMounted({});

  const [picking, setPicking] = useState(null);
  const isLoadingReaders = fromAnyToBool(!picking || picking.isLoading);
  const isPickingReader = fromAnyToBool((isFeMultipleReadersEnabled || isBeMultipleReadersEnabled) && picking);
  const isMakingPayment = !isPickingReader;
  const resolveReader = picking?.resolve;

  const handleSelectReader = useCallback(
    (ev) => {
      resolveReader?.(ev?.value);
      setPicking(null);
    },
    [resolveReader],
  );

  /** @type {function(*, *?): void} */
  const signal = useCallback(
    (status, ev) => {
      L.debug('signal()', status, ev);

      setStatus(status);

      const evChange = {
        ...(ev ?? void 1),
        status,
        message: fromAnyToString(ev?.message), // making sure the message is a string
      };

      L_POS.debug('PosReaderDialog() > signal()', status, evChange, '<-', ev);
      call(`${CTAG}.onChange`, onChange, evChange);
    },
    [onChange],
  );

  const handleAttempt = useCallback(() => {
    const FTAG = 'handleAttempt()';

    // reset state to initial values
    start();
    hideRetry();
    incrementAttempts();
    setUnexpectedError('');
    clearGiveUp();
    setRef(codeRef, '');
    setLocalPaymentIntentId(null);

    call(`${CTAG}.onInit`, onInit, onInit);

    // setup same error handler for both tryAsync calls
    const onCatch = (error) => {
      L.error(FTAG, 'onCatch()', error);

      stop();

      const message = fromAnyToErrorMessage(error);
      setUnexpectedError(message);
      captureLoopError({ name: 'onCatch', error });
      signal(PRDS.error, { message, error });
    };

    // this is the main flow, helper is used to make it easier to handle multiple async calls in a hook
    tryAsync(onCatch, async () => {
      // step 1, inform parent the flow has started

      L.info(FTAG, MSG_INIT);
      signal(PRDS.open, { message: MSG_INIT });

      // step 2, determine reader(s), handle errors and inform parent

      const pickedReaderPromise = (async () => {
        setPicking({ isLoading: true });

        const readerInfoOrInfosPromise = determinePosReaderId({ salonId, beFindReader });
        readerInfoOrInfosPromise.finally(() => setPicking({ isLoading: false }));

        const readerInfoOrInfos = await readerInfoOrInfosPromise;
        // NOTE: ensure we always operate with array
        // BE may return either a single reader object or an array of readers
        const readersArray = asArray(readerInfoOrInfos);
        const activeReaders = readersArray.filter((r) => r?.isActive);
        setActives(activeReaders);

        const generalReaderStatus = readerInfoOrInfos?.generalReaderStatus;

        // if no array, it means it's the old single reader endpoint, just return the info for the single one
        const hasMultipleReaders = Array.isArray(readerInfoOrInfos);
        if (!hasMultipleReaders && !generalReaderStatus) return readerInfoOrInfos;

        // only consider those marked active i.e. either online or offline, if not multiple, just return the one (or none)
        const actives = readerInfoOrInfos.filter((item) => item.isActive);
        if (READERS_LIMIT_TO_SHOW_PICKER_DIALOG > actives.length && !generalReaderStatus && firstOf(actives)?.isOnline) return firstOf(actives);

        // NOTE: this is where it gets tricky
        // a dialog should be displayed and this function should not return until one is picked
        // so it needs to use a Promise to block execution until picked
        // and can use the resolve function or null instead of a regular true/false boolean to display the dialog

        // can't allow to reject in this flow, so a non-valid info resolved will be filtered out later

        // NOTE: if you just ask useState to store a function, it will automatically call it - BAD
        // React shouldn't automatically call a function you want to store, so it's a part of an object
        const promise = new Promise((resolve) => void setPicking({ resolve, actives, status: generalReaderStatus }));

        // after all is done, turn it to 0
        promise.finally(() => setPicking(null));

        return promise;
      })();

      const pickedReader = await pickedReaderPromise;
      const { readerId, readerStatus, message: findMessage, isOnline, isNotOnline } = pickedReader ?? {};

      L.debug('()', 'pickedReader:', pickedReader);

      if (!pickedReader || isNotOnline) {
        showCancel();
        stop();
        return void signal(PRDS.disconnected, { message: MSG_OFFLINE, readerStatus, isOnline, isNotOnline });
      }

      if (findMessage) {
        // NOTE: the above isNotOnline check might make this one obsolete, but keep it for now, just in case
        stop();
        return void signal(PRDS.error, { message: findMessage, readerStatus, isOnline, isNotOnline });
      }

      setLocalReaderId(readerId);
      L.info(FTAG, MSG_CONNECT);

      // step 3, ask parent to initiate the appropriate payment and wait for the result, handle errors if necessary

      const connectedMessage = `${MSG_CONNECT} Status: ${readerStatus}`;
      signal(PRDS.connected, { message: connectedMessage, readerId, readerStatus });

      // Don't just notify parent of status here, call its onAction callback to perform the payment as well
      const connectedOptions = { message: connectedMessage, readerId, readerStatus };
      const connectedResult = await call(`${CTAG}.onAction`, onAction, connectedOptions);
      const { paymentIntentId, errorMessage: initErrorMessage } = asObject(connectedResult);

      L.debug(FTAG, 'paymentIntentId:', paymentIntentId, 'initErrorMessage:', initErrorMessage);
      setLocalPaymentIntentId(paymentIntentId);

      if (initErrorMessage) {
        stop();
        showCancel();
        showRetry();
        setUnexpectedError(initErrorMessage);
        setRef(codeRef, POS_FLOW_CODE.init);
        return void signal(PRDS.error, { code: deRef(codeRef), message: initErrorMessage });
      }

      // step 4, periodically check if reader status changed to pass/fail condition
      // NOTE: there are two parallel ways to do it: pusher channel and periodic fetch

      const unsubscribePusher = FLAG.pusherPosReaderDialog
        ? PUSHER.single({
            salonId,
            eventName: 'pos_payment',
            log: true,
            fn: (pusherResult) => {
              const FTAG = 'checkPusherPosStatus()';
              const ev = fromPusherDtoToPosPaymentEvent(pusherResult);

              L.debug(FTAG, ev, '<-', pusherResult);

              const processResult = processFetchOrPusherResponse({
                // optional, not necessary for the processing, just nice to have them logged in case of error
                pusherResult,
                ev,

                // these will come from the periodic fetch response
                errorMessage: ev?.errorMessage,
                code: ev?.code,
                message: ev?.message,

                tag: FTAG,

                // these should be shared i.e. be same with the fetch handler
                codeRef,
                showCancel,
                signal,
                paymentIntentId,
                setDoneEvent,
                hideCancel,
                showClose,
                closeDelay,
                onDone,
                status,
                doneEvent,
                showRetry,
              });

              // this must be done by either the pusher or the fetch handlers
              if (PROCESSING_CODE.done === processResult.code || PROCESSING_CODE.unhandled === processResult.code) {
                unsubscribePusher();
              }
            },
          })
        : alwaysNoop;

      // this is the kickstart for the periodic loop
      loop(
        {
          delay: DELAY.autohide,
          onDone: stop,

          // use block to wait for the result before next attempt since overlapping requests will be merged by middleware
          block: true,

          // tests if the flow still needs to wait for the status from BE
          // there's a hard limit to waiting / status attempts from BE after which FE should give up with a message
          onTest: (options) => {
            const FTAG = 'checkShouldContinue()';

            if (isFalsy(options?.index < MAX_ATTEMPTS)) {
              L.warn(FTAG, 'Reached MAX_ATTEMPTS:', MAX_ATTEMPTS, 'giving up...');
              giveUp();
              return false;
            }

            const code = deRef(codeRef);
            const isMounted = deRef(isMountedRef);
            const shouldContinue = isMounted && (isNotEnumVal(POS_CODE, code) || POS_CODE.waiting === code);

            L.debug(FTAG, { code, isMounted }, 'shouldContinue:', shouldContinue);
            return shouldContinue;
          },
        },
        () =>
          deRef(isMountedRef) &&
          tryAsync(onCatch, async () => {
            const FTAG = 'checkFetchPosStatus()';

            const fetchResult = await beGetPosIntentStatus({ salonId, readerId, paymentIntentId });
            const ev = fromDtoToPosPaymentEvent(fetchResult);

            const processResult = processFetchOrPusherResponse({
              // optional, not necessary for the processing, just nice to have them logged in case of error
              fetchResult,
              ev,

              // these will come from the periodic fetch response
              errorMessage: ev?.errorMessage,
              code: ev?.code,
              message: ev?.message,

              tag: FTAG,

              // these should be shared i.e. be same with the pusher handler
              codeRef,
              showCancel,
              signal,
              paymentIntentId,
              setDoneEvent,
              hideCancel,
              showClose,
              closeDelay,
              onDone,
              status,
              doneEvent,
              showRetry,
            });

            // this must be done by either the pusher or the fetch handlers
            if (PROCESSING_CODE.done === processResult.code) unsubscribePusher();
          }),
      );
    });
  }, [
    start,
    hideRetry,
    incrementAttempts,
    setUnexpectedError,
    clearGiveUp,
    setLocalPaymentIntentId,
    onInit,
    stop,
    signal,
    salonId,
    beFindReader,
    setLocalReaderId,
    onAction,
    showCancel,
    showRetry,
    setDoneEvent,
    hideCancel,
    showClose,
    closeDelay,
    onDone,
    status,
    doneEvent,
    isMountedRef,
    giveUp,
    beGetPosIntentStatus,
  ]);

  const [, handleCancelV1] = useAfn(
    async (ev) => {
      try {
        if (rsBeInitReaderPayment) {
          L.warn('handleCancel()', 'Ignoring cancel attempt while waiting for intent ID');
          return;
        }

        // NOTE: currently there isn't much to display on FE if this call to BE fails

        signal(PRDS.cancel);
        call(`${CTAG}.onDone`, onDone, { ...ev, status, canceled: true });

        // SEE: https://getslick.atlassian.net/browse/GS-11044
        if (localReaderId) {
          const cancelOptions = {
            readerId: localReaderId,
            paymentIntentId: localPaymentIntentId,
            umsg: { fn: umsgCancelPayment, arg: { intentId: localPaymentIntentId } },
          };
          await beCancelPayment(cancelOptions);
        }

        // SEE: https://getslick.atlassian.net/browse/GS-11856
        feDelPosIntentId();

        //
      } catch (e) {
        L.warn('handleCancel()', e);
        setUnexpectedError(fromAnyToErrorMessage(e));
      }
    },
    [rsBeInitReaderPayment, signal, onDone, status, beCancelPayment, localReaderId, localPaymentIntentId, feDelPosIntentId, setUnexpectedError],
  );

  const [, handleCancelV2] = useAfn(
    async (ev) => {
      if (rsBeInitReaderPayment) {
        L.warn('handleCancel()', 'Ignoring cancel attempt while waiting for intent ID');
        return;
      }

      const cancelOptions = {
        readerId: localReaderId,
        paymentIntentId: localPaymentIntentId,
        umsg: { fn: umsgCancelPayment, arg: { intentId: localPaymentIntentId } },
      };

      try {
        // NOTE: currently there isn't much to display on FE if this call to BE fails
        // SEE: https://getslick.atlassian.net/browse/GS-11044
        if (localReaderId) await beCancelPayment(cancelOptions);

        // SEE: https://getslick.atlassian.net/browse/GS-11856
        feDelPosIntentId();

        //
      } catch (e) {
        L.warn('handleCancel()', e);
        setUnexpectedError(fromAnyToErrorMessage(e));
        captureFailedCancel({ name: 'handleCancel', flags: salonFlags, error: e, options: cancelOptions });
      } finally {
        signal(PRDS.cancel);
        const event = { ...ev, status, canceled: true };
        L_POS.debug('PosReaderDialog.handleCancel()', 'event:', event);
        call(`${CTAG}.onDone`, onDone, event);
      }
    },
    [rsBeInitReaderPayment, beCancelPayment, localReaderId, localPaymentIntentId, feDelPosIntentId, setUnexpectedError, salonFlags, signal, status, onDone],
  );

  const handleClose = useCallback(
    (ev) => {
      signal(PRDS.closed);

      if (USE_IS_CONFIRMING) {
        L_POS.debug('PosReaderDialog.handleClose()', 'doneEvent:', doneEvent);
        call(`${CTAG}.onDone`, onDone, doneEvent);
      } else {
        call(`${CTAG}.onDone`, onDone, { ...ev, status });
      }
    },
    [signal, doneEvent, onDone, status],
  );

  useEffect(() => {
    // NOTE: it is important to wait until the dialog is open before trying to charge users
    // SEE: https://getslick.atlassian.net/browse/GS-11101
    if (!open) return;

    const isFirstAttempt = !(attempts < 1);
    if (!autoStart || isActive || isFirstAttempt) return;

    L.debug('useEffect()', { autoStart, isActive, isFirstAttempt, attempts });
    handleAttempt();
  }, [handleAttempt, isActive, attempts, incrementAttempts, salonId, autoStart, open]);

  const handleCancel = USE_IS_CONFIRMING ? handleCancelV2 : handleCancelV1;

  const handleClosePicker = useCallback(() => {
    giveUp();
    handleCancel({ tag: 'handleClosePicker' });

    resolveReader?.(null);
    setPicking(null);

    setStatus(PRDS.closed);
  }, [giveUp, handleCancel, resolveReader]);

  // NOTE: should not be able to cancel while waiting for intent ID
  const isCancelRendered = !rsBeInitReaderPayment && isCancelAvailable;

  L.debug('()', { open, status, closeDelay, USE_IS_CONFIRMING }, { isLoadingReaders, picking });

  if (!open) return null;

  // it should be one dialog or the other, not both  displayed at the same time, but they can be rendered
  return (
    <>
      {(actives.length > 1 || (actives.length === 1 && !firstOf(actives)?.isOnline)) && (
        <CardReadersModal
          tag="PosReaderDialog"
          isOpen={isPickingReader}
          onClose={handleClosePicker}
          onSelect={handleSelectReader}
          readers={picking?.actives}
          generalReaderStatus={picking?.status}
          isLoading={isLoadingReaders}
        />
      )}
      {actives.length === 1 && firstOf(actives)?.isOnline && (
        <SalonModal isOpen={status !== PRDS.closed}>
          <SalonModal.Body>
            <DialogMessage>
              <PosStateMessage posStep={status} price={price} />
            </DialogMessage>
          </SalonModal.Body>
        </SalonModal>
      )}
      <SalonModal tag="PosReaderDialog" isOpen={isMakingPayment} disabled={status !== PRDS.success}>
        <SalonModal.Body>
          {reactChildrenUnlike(PosReaderPriceLine, children)}

          <div data-bem="PosReaderDialog">
            <div data-bem="PosReaderDialog__error-message--unexpected" className={CN.errorMessage}>
              {isTruthy(unexpectedError) && <DialogMessage>{unexpectedError}</DialogMessage>}
            </div>
            {isGivenUp && (
              <DialogMessage>
                <p data-bem="PosReaderDialog__error-message--given-up" className={CN.timeoutMessage}>
                  {t('composite.posReaderDialog.timeOutMessage1')}
                  <br />
                  {t('composite.posReaderDialog.timeOutMessage1')}
                </p>
              </DialogMessage>
            )}
          </div>

          <SalonFlag name={SF.tmpPosShowPriceLines}>
            <SalonFlag.IfEnabled>{reactChildrenLike(PosReaderPriceLine, children)}</SalonFlag.IfEnabled>
          </SalonFlag>
        </SalonModal.Body>

        <SalonModal.Footer>
          {isCloseRendered && (
            <Button data-bem="PosReaderDialog__close" variant="primary" casing="dialog" tabIndex={0} onClick={handleClose} type="button">
              <span className={CN.buttonSpace}>{t('common.buttons.close')}</span>
            </Button>
          )}

          {isCancelRendered && (
            <Button data-bem="PosReaderDialog__cancel" variant="alert" casing="dialog" tabIndex={0} onClick={handleCancel} type="button">
              <span className={CN.buttonSpace}>{t('composite.posReaderDialog.cancelPayment')}</span>
            </Button>
          )}

          {isRetryRendered && (
            <Button data-bem="PosReaderDialog__continue" variant="primary" casing="dialog" tabIndex={0} onClick={handleAttempt}>
              <span className={CN.buttonSpace}>{t('composite.posReaderDialog.tryAgain')}</span>
            </Button>
          )}
        </SalonModal.Footer>
      </SalonModal>
    </>
  );
};

// noinspection JSUnusedGlobalSymbols
const PosReaderDialogHoc = connect(
  (state) => ({
    salonId: selectCurrentSalonId(state),
    posFlow: selectCurrentSalonPosFlow(state),
    rsBeInitReaderPayment: selectRsBeInitReaderPayment(state),
    salonFlags: selectCurrentSalonFlags(state),
  }),
  mapActionsToDispatchToProps({
    beCancelPayment,
    beFindReaderV1,
    beFindReaderV3,
    beGetPosIntentStatus,
    feDelPosIntentId,
  }),
)(PosReaderDialog);

export default PosReaderDialogHoc;
